/* agmf-desktop-header.js - the header gets out of the way on the desktop page.
   ─────────────────────────────────────────────────────────────────────────
   Dorian, 2026-09-02: "when scrolling down after a bit (you decide when),
   hide the headers, and put it back by the moment the user is again
   scrolling up". Desktop only (html.agdesk-on) - the phone page is untouched.

   WHAT SCROLLS. On the desktop page the window never moves: the two panes
   (.agdesk-media and .product-info.agdesk-buy, pdp-desk-3col.js) are the
   scrollers, driven in lockstep by that file's wheel router. The signal here
   is the WHEEL (and keys, and a finger) rather than either scroller - see
   "THE DIRECTION COMES FROM THE WHEEL" below for the measurement that forced
   that, and why a scroll-derived version cannot be made to behave.

   WHEN. 120px of downward travel, and not within the first 90px of the top:
   a nudge to look at the next picture is not a decision to leave the header.
   Any upward movement brings it back at once - "by the moment" - as does
   arriving back at the top.

   HOW. Two things move together, as one class on <html>:
     - the announcement bar + the sticky header slide up by their combined
       height (--agmf-hdr-h, measured here);
     - the product section takes the same negative top margin so the panes
       follow the header up, and their height (which reads --agdesk-top)
       grows by the same amount, so the footer strip keeps its place.
   The height and the slide animate TOGETHER: agilite-media-flow.css registers
   --agdesk-top (@property) and transitions it with the same timing as the
   section's margin, so the document never changes length mid-slide and the
   folded footer strip stays exactly where it is. (The first version changed
   the height in one step and the strip dropped off-screen for a third of a
   second - Dorian saw it.) Browsers without @property get the one-step
   behaviour back.

   A SIDE EFFECT THAT IS A FIX. pdp-desk-3col.js measures --agdesk-top once,
   BEFORE it adds html.agdesk-on; the desktop rule that zeroes the section's
   top padding is gated on that class, so the panes were measured 16px lower
   than they end up, ran 16px short, and the folded footer showed 60px instead
   of the 44 it was set to. This file re-measures once the class is on.

   THE LEAD SCROLLS AWAY LIKE PAGE CONTENT (Dorian 2026-10-12: "the timer for
   sales that is on the top of the product pages ... needs to be not sticky
   and slide with the page"). The "SALE ENDS IN" countdown was put into the
   product sections from the Shopify admin, above the product grid. On every
   other page the document scrolls and it leaves with it. Here the panes
   scroll and the document does not, so nothing ever moved it: it stayed
   between the header and the panes. Once the header slid away it sat alone
   at the very top of the screen (measured on live Magnetix: y 146 -> 23 after
   700px of feed).

   "The lead" is whatever the product section holds above the grid: today
   that countdown, and nothing at all on a page without it, where every rule
   below does nothing. It behaves as the top of a page does:
     - a downward wheel moves the lead away first, pixel for pixel, and the
       panes take only what is left of that wheel once it is gone;
     - an upward wheel brings it back only once both panes are at their top;
     - the panes moved by something other than the wheel (the keyboard, a
       touch screen, "Go back to top") take it away or put it back in one
       slide.
   It is the same machinery as the header's: the section takes the lead's
   height as negative top margin (--agmf-lead-off, under html.agmf-lead-out)
   and --agdesk-top drops by it, so the panes grow by exactly what left and
   the folded footer strip stays where it is.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.__agmfDesktopHeader) return;
  window.__agmfDesktopHeader = true;

  var root = document.documentElement;
  var HIDE_AFTER = 120;   // px of downward travel before the header goes
  var NEAR_TOP = 90;      // never hide this close to the top of the pane
  var LEAD_MIN = 12;      // a lead shorter than this is a stray gap, not content

  var hidden = false, hdrH = 0, restTop = 0, wired = false, slideTimer = 0;
  var lead = 0;           // px of the lead currently moved out of view
  var routedAt = 0;       // when the wheel router last asked take()

  // The --agdesk-top transition lives only under this class, for the length
  // of one slide; left standing it also animated the page-load measure.
  function sliding() {
    root.classList.add('agmf-hdr-sliding');
    clearTimeout(slideTimer);
    slideTimer = setTimeout(function () { root.classList.remove('agmf-hdr-sliding'); }, 420);
  }

  function desktop() { return root.classList.contains('agdesk-on'); }
  function header() { return document.querySelector('header.shopify-section--header'); }
  function grid() {
    var m = document.querySelector('.shopify-section--main-product');
    // the gear builder's page has no .product grid; its own grid holds the panes
    return (m && m.querySelector('.product')) || document.querySelector('.agb-layout.agdesk-grid');
  }

  // ── HOW FAR THE HEADER HAS TO TRAVEL: A SUM OF HEIGHTS, NEVER A POSITION.
  // The header is `position:sticky; top:0` (sections/header.liquid under
  // enable_sticky), so once the document has scrolled at all, EVERY position
  // it reports is the scroll, not the header: at scrollY 545 its rect.bottom
  // is 104 and `rect.bottom + scrollY` is 649, where the truth is 143 - and
  // offsetTop is no refuge, it tracks the stuck position too (both measured
  // in Chromium). The old formula therefore asked the page to translate 649px
  // and the product section to take margin-top:-649px, and the page lurched
  // half a screen; the footer peek scrolls the document to ~545, and a wheel
  // over the folded strip goes to the document as well, so this was one notch
  // away at any time.
  //
  // Heights cannot lie: whatever the scroll, whatever is stuck, the bar plus
  // the header is how much sits above the page. The bar counts only while it
  // scrolls away with the page (it is not sticky here); a sticky bar stays
  // put and has nothing to give back.
  function measure() {
    var h = header();
    if (!h) { hdrH = 0; }
    else {
      var bar = document.querySelector('[id$="__announcement-bar"]');
      var barH = (bar && getComputedStyle(bar).position !== 'sticky') ? bar.offsetHeight : 0;
      hdrH = Math.max(0, Math.round(barH + h.offsetHeight));
    }
    root.style.setProperty('--agmf-hdr-h', hdrH + 'px');
    return hdrH;
  }

  // the same formula pdp-desk-3col.js uses for --agdesk-top, taken now.
  function gridTop() {
    var g = grid();
    return g ? Math.max(0, Math.round(g.getBoundingClientRect().top + window.scrollY)) : 0;
  }
  function setTop(px) { root.style.setProperty('--agdesk-top', Math.max(0, Math.round(px)) + 'px'); }

  // restTop = where the grid sits with the header showing and the lead in
  // place. Taken while the header shows (never mid-slide), and it is the
  // number every state derives from: restTop - (hidden ? hdrH : 0) - lead.
  // The transition on --agdesk-top does the in-between.
  function syncTop() {
    if (!grid() || hidden) return;
    restTop = gridTop() + lead;
    setTop(restTop - lead);
  }

  // ── THE LEAD (see the header). Measured, never assumed: the countdown's
  // picture arrives after the page, and the lead grows when it does. The
  // section's margin moves the section and the grid together, so the gap
  // between their tops is the lead's height whatever has slid.
  function section() { return document.querySelector('.shopify-section--main-product'); }
  function leadHeight() {
    var s = section(), g = grid();
    if (!s || !g || !s.contains(g)) return 0;
    var h = Math.round(g.getBoundingClientRect().top - s.getBoundingClientRect().top);
    return h >= LEAD_MIN ? h : 0;
  }
  function setLead(px) {
    px = Math.max(0, Math.round(px));
    if (px === lead) return;
    if (!restTop && !hidden) restTop = gridTop() + lead;
    lead = px;
    sliding();                          // the panes' top glides with the margin
    root.style.setProperty('--agmf-lead-off', lead + 'px');
    root.classList.toggle('agmf-lead-out', lead > 0);
    setTop(restTop - (hidden ? hdrH : 0) - lead);
  }

  // The wheel router (pdp-desk-3col.js) hands every wheel here before the
  // panes move, and moves the panes by whatever comes back. `atTop` = both
  // panes are at their top, the only place the lead may come back.
  function take(dy, atTop) {
    routedAt = Date.now();
    if (!desktop() || !dy) return dy;
    var max = leadHeight();
    if (!max) { if (lead) setLead(0); return dy; }
    if (dy > 0) {
      if (lead >= max) return dy;
      var out = Math.min(dy, max - lead);
      setLead(lead + out);
      return dy - out;
    }
    if (!atTop || lead <= 0) return dy;
    var back = Math.min(-dy, lead);
    setLead(lead - back);
    return dy + back;
  }

  // The panes moved WITHOUT the wheel - the keyboard, a touch screen, the
  // "Go back to top" button. A wheel already spoke to take(), so any pane
  // scroll close behind one is its echo and is left alone.
  function onPaneScroll(e) {
    var p = mediaPane();
    if (!p || e.target !== p || !desktop()) return;
    if (Date.now() - routedAt < 400) return;
    var max = leadHeight();
    if (!max) return;
    if (p.scrollTop > 4) { if (lead < max) setLead(max); }
    else if (lead > 0) setLead(0);
  }

  // ── AND IT HAS TO KEEP BEING TRUE. --agdesk-top is written ONCE by
  // pdp-desk-3col.js (before it even adds the class) and once here, and the
  // page is not finished settling at either moment: measured on a fresh load
  // 2026-09-02, the variable held 191px for a grid that had come to rest at
  // 143, so the panes ran 48px short and the folded footer showed 84px where
  // the design says 36. Nothing was ever going to correct it.
  //
  // So the value is now checked against the page rather than trusted: at rest
  // (header showing, document at the top, no slide in flight) the grid's own
  // top wins. Two reads, and only when something might have moved.
  function guardTop() {
    if (!desktop()) return;
    // the header's own height is checked here too: it is read once at boot,
    // and the page is not finished then. Measured 2026-09-03, after the bar
    // was made shorter: the variable still said 144 for a header that had
    // come to rest at 109, so a hide would have travelled 35px too far.
    measure();
    if (hidden || window.scrollY > 4 || !grid()) return;
    // mid-slide the grid is between two places, and neither is "at rest"
    if (root.classList.contains('agmf-hdr-sliding')) return;
    var max = leadHeight();
    if (lead > max) { setLead(max); return; }   // the lead got shorter
    var t = gridTop() + lead;
    if (Math.abs(t - restTop) > 1) { restTop = t; setTop(t - lead); }
  }
  var guardAt = 0;
  function guardSoon() {
    var n = Date.now();
    if (n - guardAt < 400) return;
    guardAt = n;
    guardTop();
  }

  function hide() {
    if (hidden || !desktop()) return;
    // The whole geometry assumes the page itself is at the top: --agdesk-top
    // is spent as a VIEWPORT offset (100vh - top - peek) while the grid's top
    // is a document one. With the footer open the document is ~545 down, the
    // two stop meaning the same thing, and the panes would be re-sized against
    // a page that has moved. The footer is the shopper's business then, not
    // the header's.
    if (window.scrollY > 4) return;
    measure();                          // while it still shows: the rect
    if (!restTop) restTop = gridTop() + lead;
    hidden = true;
    sliding();
    root.classList.add('agmf-hdr-hidden');
    setTop(restTop - hdrH - lead);      // animates alongside the margin
  }

  function show() {
    if (!hidden) return;
    hidden = false;
    sliding();
    root.classList.remove('agmf-hdr-hidden');
    setTop(restTop - lead);             // same timing back
  }

  // ── THE DIRECTION COMES FROM THE WHEEL, NOT FROM scrollTop (Dorian
  // 2026-09-02: the appearing/disappearing banner "is a bit glitchy").
  // MEASURED CAUSE: hiding the header GROWS both panes by 143px, which lowers
  // their maximum scroll, which CLAMPS a pane sitting near its end - the buy
  // column jumped from 1210 to 1067 with nobody touching it. That is a scroll
  // event carrying a 143px NEGATIVE delta, and the old handler read every
  // negative delta as "the shopper is scrolling up" and put the header back.
  // Header back -> panes shrink -> the next notch of the wheel hides it again.
  // A flicker driven by the layout, not by the person.
  //
  // The wheel (and the keys, and a finger) is the INTENT, and intent is what
  // he asked for: gone after a bit of scrolling down, back the moment he
  // scrolls up. A reflow cannot forge one, so the loop cannot form at all -
  // this is not a damped version of the old rule, it is a different signal.
  // ── A WHEEL WOBBLES, AND A WOBBLE IS NOT A DIRECTION (Dorian 2026-09-07:
  // the top bar appearing/disappearing "can be a bit buggy, but less").
  // Inertial scrolling on a trackpad emits stray opposite-sign pixels inside
  // a single downward flick - one or two, between the real ones. Every such
  // pixel used to be a full `up()`: the header came straight back and the
  // 120px counter reset, so the bar could leave and return inside one gesture
  // without the shopper ever scrolling up. The TOUCH path has carried a 4px
  // deadzone since it was written, for exactly this; the wheel never got one.
  //
  // So: ignore the noise, and ask for a little real upward travel before
  // coming back. 24px is under a third of one wheel notch and a fraction of a
  // deliberate trackpad drag, so "the moment the user is again scrolling up"
  // still holds - it refuses a wobble and nothing else. A KEY is never a
  // wobble and shows at once, as does arriving back at the top.
  // ── A CHANGE OF MIND TAKES A MOMENT (Dorian 2026-09-08: "when I scroll
  //    down sometimes it suddenly is laggy and makes the banner reappear for
  //    a bit while I didn't intend to ... maybe wait a bit when scrolling up
  //    before deciding to put back the banner?") ─────────────────────────
  // The 4px deadzone above catches a wobble that arrives as its own event.
  // It cannot catch one that arrives inside a laggy frame: while the page is
  // busy (three players booting, a lap of cards recycling), the browser
  // holds the wheel's events and hands them over merged, so the handful of
  // reverse pixels a trackpad sheds at the end of a flick reach us as ONE
  // event of twenty or thirty - past the deadzone, past the 24px bar, and
  // the header is back. The next 120px of the same downward gesture hide it
  // again: gone, back, gone, nobody asked for any of it.
  //
  // So an upward event is read against the gesture it arrives in. Inside
  // SETTLE_MS of a downward one it IS that gesture - its tail, its bounce,
  // its merged wobble - and it neither shows the header nor resets the
  // downward count. Past that, it is a climb, and a climb needs SHOW_AFTER
  // of travel inside CLIMB_WINDOW: a single mouse notch (100px) still brings
  // the header back at once, a deliberate two-finger drag brings it back
  // after a finger's width, and stray pixels a second apart never add up.
  var run = 0, runUp = 0, lastDownAt = 0, lastUpAt = 0;
  var NOISE = 4;          // px of wheel movement that mean nothing
  var SHOW_AFTER = 56;    // px of real upward travel before it returns
  var SETTLE_MS = 160;    // an "up" this soon after a "down" is the same gesture
  var CLIMB_WINDOW = 400; // upward travel older than this no longer counts

  function mediaPane() { return document.querySelector('.agdesk-media'); }
  function nearTop() { var p = mediaPane(); return !p || p.scrollTop <= NEAR_TOP; }
  function dialogOpen() { return root.classList.contains('agmf-noscroll'); }

  function down(px) {
    if (!desktop() || dialogOpen()) return;
    guardSoon();
    lastDownAt = Date.now();
    runUp = 0;                                    // going down ends any climb
    if (nearTop()) { run = 0; show(); return; }   // at the top the header belongs there
    run += px;
    if (!hidden && run >= HIDE_AFTER) hide();
  }
  // `px` absent = a deliberate press (a key), which is a decision, not travel.
  function up(px) {
    if (!desktop()) return;
    if (px == null) { run = 0; runUp = 0; show(); return; }
    var now = Date.now();
    if (now - lastDownAt < SETTLE_MS) return;     // the tail of a downward gesture
    if (now - lastUpAt > CLIMB_WINDOW) runUp = 0; // a climb is continuous
    lastUpAt = now;
    run = 0;                                      // a real climb ends the descent
    runUp += px;
    if (runUp >= SHOW_AFTER) show();
  }

  function onWheel(e) {
    var dy = e.deltaY;
    if (!dy) return;
    if (e.deltaMode === 1) dy *= 16;                    // lines
    else if (e.deltaMode === 2) dy *= window.innerHeight; // pages
    if (Math.abs(dy) < NOISE) return;                  // a wobble, not a direction
    if (dy > 0) down(dy); else up(-dy);
  }

  var touchY = 0;
  function onTouchStart(e) { touchY = e.touches && e.touches[0] ? e.touches[0].clientY : 0; }
  function onTouchMove(e) {
    if (!touchY || !e.touches || !e.touches[0]) return;
    var y = e.touches[0].clientY, dy = touchY - y;
    if (Math.abs(dy) < NOISE) return;
    touchY = y;
    if (dy > 0) down(dy); else up(-dy);
  }

  var DOWN_KEYS = { ArrowDown: 60, PageDown: 400, End: 4000, ' ': 400, Spacebar: 400 };
  function onKey(e) {
    if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'Home') { up(); return; }
    var px = DOWN_KEYS[e.key];
    if (px) down(px);
  }

  function wire() {
    if (wired || !mediaPane()) return;
    wired = true;
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('keydown', onKey);
    // capture: a scroll does not bubble, and the media pane is rebuilt each
    // time the desktop layout is entered
    document.addEventListener('scroll', onPaneScroll, true);
    measure();
    syncTop();                          // the post-class measure (see header)
    // the page is still settling here: check again once it has stopped
    setTimeout(guardTop, 300);
    setTimeout(guardTop, 1200);
    setTimeout(guardTop, 3000);
    window.addEventListener('load', function () { setTimeout(guardTop, 200); });
    var reflow = 0;
    window.addEventListener('resize', function () {
      measure();
      if (hidden) setTop(restTop - hdrH - lead); else syncTop();
      clearTimeout(reflow);
      reflow = setTimeout(guardTop, 250);   // after the reflow has landed
    });
  }

  function onClassChange() {
    if (desktop()) { wire(); return; }
    if (hidden) show();
    // the phone page scrolls the document: the lead is ordinary content there
    if (lead) {
      lead = 0;
      root.style.removeProperty('--agmf-lead-off');
      root.classList.remove('agmf-lead-out');
    }
  }

  if (desktop()) wire();
  new MutationObserver(onClassChange).observe(root, { attributes: true, attributeFilter: ['class'] });

  window.AGMFDesktopHeader = { hide: hide, show: show, measure: measure, syncTop: syncTop, take: take,
                              isHidden: function () { return hidden; },
                              leadOut: function () { return lead; } };
})();
